// Per-browser UI settings (the look), mirrored to the account once signed in (E4+).
// Calm motion follows the operating system's "reduce motion" setting only.
// Remembered on this device only with the visitor's "preferences" consent; otherwise they last
// for the visit, and withdrawing consent deletes what was stored.
import { create } from "zustand";
import { hasConsent, onConsentChange } from "./consent";

/** Studio Feel, Hifi Feel, Eigenbloom (all the studio components, different looks) or the Zoo. */
export type Skin = "studio" | "hifi" | "zoo" | "eigenbloom";
export const SKINS: readonly Skin[] = ["studio", "hifi", "zoo", "eigenbloom"];
export const isSkin = (s: unknown): s is Skin => SKINS.includes(s as Skin);

/** The shape the Eigenbloom look draws (one of its library shapes). */
export const DEFAULT_SHAPE = "heart";

interface Stored {
  skin: Skin;
  shape: string;
}

interface Settings extends Stored {
  setSkin: (s: Skin) => void;
  setShape: (shape: string) => void;
}

const KEY = "eb.settings";

function load(): Stored {
  const fallback: Stored = { skin: "studio", shape: DEFAULT_SHAPE };
  if (!hasConsent("preferences")) return fallback;
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "{}") as Partial<Stored>;
    return {
      skin: isSkin(raw.skin) ? raw.skin : "studio",
      shape: typeof raw.shape === "string" && /^[a-z0-9-]{1,40}$/.test(raw.shape) ? raw.shape : DEFAULT_SHAPE,
    };
  } catch {
    return fallback;
  }
}

function save(s: Stored) {
  if (!hasConsent("preferences")) return;
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* storage unavailable */
  }
}

export const useSettings = create<Settings>((set, get) => ({
  ...load(),
  setSkin: (skin) => {
    set({ skin });
    save({ skin, shape: get().shape });
  },
  setShape: (shape) => {
    set({ shape });
    save({ skin: get().skin, shape });
  },
}));

/** True when animations should be minimal (the operating system's "reduce motion" setting). */
export function prefersCalm(): boolean {
  return typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Consent given → keep the current settings; withdrawn → forget them on this device.
onConsentChange((c) => {
  if (c.preferences) {
    const { skin, shape } = useSettings.getState();
    save({ skin, shape });
  } else {
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* storage unavailable */
    }
  }
});
